import React, { FC, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId, TaskId } from 'api/models';
import { TaskTable } from 'components/molecules/tasks/task-table';
import { toastService } from 'components/molecules/toast';
import { Task } from 'data/tasks';
import {
  createMultipleSubTasksSagaAction,
  createSubTaskSagaAction,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { useTasksByIds } from 'data/tasks/hooks';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslation } from 'hooks/use-translation';
import { useUpdateSubTaskOrderMutation } from 'queries';
import { getTaskDetailQueryKey } from 'queries/task';

interface Props {
  taskId: number;
  projectId: number;
  data: number[];
  isExpanded: boolean;
}

export const SubTasksList: FC<Props> = (props) => {
  const { data = [], taskId, projectId, isExpanded } = props;
  const dispatch = useDispatch();
  const tasks = useTasksByIds(ProjectId(projectId), data);
  const { mutate } = useUpdateSubTaskOrderMutation(taskId);
  const queryClient = useQueryClient();

  const handleAdd = (taskName: string) => {
    dispatch(
      createSubTaskSagaAction({
        projectId,
        taskName,
        parentTask: taskId,
        queryClient,
      }),
    );

    return Promise.resolve();
  };

  const handleFormAdd = (name: string, id: number) => {
    dispatch(
      createSubTaskSagaAction({
        projectId,
        taskName: name,
        formTemplateId: id,
        parentTask: taskId,
        queryClient,
      }),
    );

    return Promise.resolve();
  };

  const handleAddMultiple = (subtasks: Array<string>) => {
    dispatch(
      createMultipleSubTasksSagaAction({
        projectId,
        subtasks: subtasks.map((subtask) => ({ taskName: subtask })),
        parentTask: taskId,
        queryClient,
      }),
    );

    return Promise.resolve();
  };

  const translatedPlaceholderText = useTranslation('placeholder.sub_task.new');
  const navigateToTask = useNavigateToTask();

  const handleTaskNameClick = ([, _projectId, _taskId]: TaskId) => {
    navigateToTask(TaskId(_projectId, _taskId));
  };
  const title = useTranslation('task.reorder.success.title');
  const message = useTranslation('task.reorder.success.message');
  const handleSort = useCallback(
    (sorted: Task[]) => {
      mutate(
        { subTaskOrder: sorted.map((t) => t.taskId) },
        {
          onSuccess: async () => {
            toastService({ title, message }).success();
            await queryClient.invalidateQueries(
              getTaskDetailQueryKey(TaskId(projectId, taskId)),
            );
            dispatch(
              updateTaskSagaAction({
                projectId,
                taskId,
                data: {
                  subTaskOrder: sorted.map((t) => t.taskId),
                },
              }),
            );
          },
        },
      );
    },
    [message, mutate, projectId, queryClient, taskId, title, dispatch],
  );

  return (
    <TaskTable
      onSort={handleSort}
      hideEmptyStateImage
      onTaskNameClick={handleTaskNameClick}
      projectId={projectId}
      data={tasks}
      handleNewTaskAdded={handleAdd}
      handleNewFormAdded={handleFormAdd}
      newRowPlaceholder={translatedPlaceholderText}
      handleMultipleTasksAdded={handleAddMultiple}
      isSubTask
      autoFocus={false}
      isExpanded={isExpanded}
      canAddFormAsSubTask
    />
  );
};
